<template>
  <v-app-bar app color="bhWhite" height="80px" class="app-toolbar">
    <div class="d-flex flex-1 align-center justify-space-between">
      <div class="d-flex align-center justify-start  flex-1">
        <v-app-bar-nav-icon @click.stop="toggleNavbar" class="d-sm-none">
          <v-icon>mdi-menu</v-icon>
        </v-app-bar-nav-icon>
        <slot name="pageTitle">
          <div class="breadcrumb">
            <v-breadcrumbs :items="breadcrumbs">
              <template v-slot:divider>
                <v-icon>mdi-chevron-right</v-icon>
              </template>
              <template v-slot:item="props">
                <router-link :to="props.item.href" v-if="!props.item.disabled">
                  <v-breadcrumbs-item
                    :class="[props.item.disabled && 'disabled']"
                    v-bind:title="props.item.text"
                  >
                    <span v-html="props.item.text"></span>
                  </v-breadcrumbs-item>
                </router-link>
                <v-breadcrumbs-item
                  :class="[props.item.disabled && 'disabled']"
                  v-bind:title="props.item.text"
                  v-else
                >
                  {{ props.item.text }}
                </v-breadcrumbs-item>
              </template>
            </v-breadcrumbs>
          </div>
        </slot>
        <div class="d-lg-flex flex-1">
          <slot name="toolbar" />
        </div>
      </div>
      <div class="d-flex align-center justify-end">
        <slot name="pageAction" />
        <v-btn
          v-if="isSurrogateLogin"
          rounded
          color="accent"
          elevation="0"
          target="_blank"
          @click="goToPreviousRole"
          class="ml-2"
          >Go to previous role</v-btn
        >
        <v-btn
          icon
          color="accent"
          elevation="0"
          class="app-toolbar__btn notifications"
          @click="showNotificationsDialog"
        >
          <v-icon size="20">bhi-bell</v-icon>
          <span v-if="isAdvisor" class="unread">{{
            unreadNotificationsGrouped() > 99
              ? '99+'
              : unreadNotificationsGrouped()
          }}</span>
          <span v-else class="unread">{{
            notifications.unread > 99 ? '99+' : notifications.unread
          }}</span>
        </v-btn>
        <router-link to="/profile/general" tag="div">
          <profile-preview-image
            :thumbnailUrl="user.thumbnailUrl"
            :profileId="profileIdAsNumber"
            :avatarClasses="['avatar', 'rounded']"
          />
        </router-link>
        <v-btn
          icon
          color="accent"
          elevation="0"
          small
          class="app-toolbar__btn logout"
          @click="logout"
        >
          <v-icon>bhi-logout</v-icon>
        </v-btn>
      </div>
    </div>
  </v-app-bar>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import ProfilePreviewImage from '../profile/ProfilePreviewImage.vue'
import Config from '@/config.json'

export default {
  name: 'AppToolbar',
  components: { ProfilePreviewImage },
  data: () => ({
    navbarShow: true,
    isLoading: false,
  }),
  computed: {
    ...mapGetters('appStates', [
      'getBreadcrumbCurrentView',
      'getBreadcrumbCurrentSubView',
    ]),
    ...mapGetters('userStates', ['getRolename', 'getProfileId']),
    ...mapState('userStates', ['notifications', 'profileId', 'user']),
    ...mapState('dictionariesStates', [
      'dictionaryBhUsersRoleIds',
      'dictionaryPracticeUserRole',
    ]),
    currentRouteName() {
      return this.$route.meta && this.$route.meta.title
        ? this.$route.meta.title
        : this.$route.name
    },
    profileIdAsNumber() {
      return parseInt(this.profileId, 10)
    },
    breadcrumbs() {
      let pathArray = this.$route.path.split('/')
      pathArray.shift()
      const breadCrumbs = []
      let breadcrumb = ''
      let lastIndexFound = 0
      let j = 0
      let breadcrumbViewIndex = 0
      for (let i = 0; i < pathArray.length; ++i) {
        breadcrumb = '/' + pathArray.slice(0, i + 1).join('/')

        // this route params are either strings (because of join above) or integers (default routing)
        if (
          (!isNaN(parseInt(pathArray[i])) &&
            Object.values(this.$route.params).includes(
              parseInt(pathArray[i]),
            )) ||
          Object.values(this.$route.params).includes(pathArray[i])
        ) {
          let disabled = this.$route.matched[i - 1]?.meta.disableLink
            ? true
            : false

          if (this.$route.matched?.[i]?.meta?.root) {
            disabled = false
          }
          breadCrumbs.push({
            href: breadcrumb,
            disabled,
            text:
              breadcrumbViewIndex === 0
                ? this.getBreadcrumbCurrentView
                : this.getBreadcrumbCurrentSubView,
          })
          breadcrumbViewIndex++
          breadcrumb = ''
        }

        if (
          this.$route.matched?.[i] &&
          Object.hasOwnProperty.call(this.$route.matched?.[i], 'meta') &&
          Object.hasOwnProperty.call(this.$route.matched?.[i]?.meta, 'title')
        ) {
          let disabled = this.$route.matched[i].meta.disableLink ? true : false

          if (this.$route.matched?.[i + 1]?.meta?.root) {
            disabled = false
            breadcrumb = '/' + pathArray.slice(0, i + 2).join('/')
          }

          breadCrumbs.push({
            href: breadcrumb,
            disabled,
            text:
              this.$route.matched?.[i]?.meta?.menuTitle ||
              this.$route.matched?.[i]?.meta?.title ||
              pathArray[i],
          })
          breadcrumb = ''
        }
      }
      return breadCrumbs
    },
    isAdvisor() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Advisor
      )
    },
    user: {
      get() {
        return this.$store.state.userStates.user
      },
      set(val) {
        this.$store.commit('userStates/updateUser', val)
      },
    },
    isSurrogateLogin() {
      return this.$store.state.userStates.surrogateLogin
    },
  },
  watch: {
    notifications: function(newNotifications, oldNotifications) {
      const bellIcon = document.querySelector('.app-toolbar__btn.notifications')
      if (newNotifications.unread > oldNotifications.unread) {
        bellIcon.classList.add('bounce')
        setTimeout(() => {
          bellIcon.classList.remove('bounce')
        }, 500)
      }
    },
  },
  methods: {
    ...mapActions('chatStates', ['unmountChatService']),
    ...mapActions('userStates', ['logoutUser']),
    ...mapActions('bhUsersStates', ['generateSurrogateAuthToken']),
    goToPreviousRole() {
      this.generateSurrogateAuthToken({
        userProfileId: this.getProfileId,
        type: 'PRIMARY_USER_AUTHENTICATION',
      }).then(res => {
        localStorage.clear()
        if (res.token) {
          window.location.href = `${Config.VUE_APP_CAS_URL_SHORT}/login?token=${res.token}`
        }
      })
    },
    toggleNavbar() {
      // this.$store.dispatch("NavbarToggle");
    },
    logout() {
      this.unmountChatService()
      this.logoutUser()
    },
    showNotificationsDialog() {
      this.$emit('open-notifications')
    },
    handleLoadStart() {
      this.isLoading = true
    },
    handleLoad() {
      this.isLoading = false
    },
    unreadNotificationsGrouped() {
      if (this.notifications && this.notifications.content) {
        const allUnreadNotificationsGrouped = this.notifications.content.reduce(
          (acc, notification) => {
            if (notification.countUnread === undefined) {
              return 0
            }
            return acc + notification.countUnread
          },
          0,
        )
        return allUnreadNotificationsGrouped
      }
      return 0
    },
  },
}
</script>
<style lang="scss">
@import '@/styles/variables.scss';

.app-toolbar {
  box-shadow: 0px 1px 0px $bh-blue--4 !important;

  .v-toolbar__content {
    padding-left: 24px;
    padding-right: 24px;
  }

  .avatar {
    margin-left: 10px;
    margin-right: 10px;
    cursor: pointer;
  }
  .notifications {
    position: relative;
    margin-left: 10px;
    .unread {
      position: absolute;
      top: 10px;
      right: 2px;
      min-width: 16px;
      padding: 0 2px;
      height: 16px;
      line-height: 16px;
      font-size: 10px;
      background: $bh-red--dark;
      display: block;
      color: $bh-white;
      border-radius: 8px;
    }
  }
}
.breadcrumb {
  .v-breadcrumbs a {
    text-decoration: none;
  }
  .v-breadcrumbs li {
    font-size: 1.42857rem;
    font-weight: bold;
    color: $bh-blue;
    &:nth-child(2n) {
      padding: 0 8px;
    }
    &.disabled {
      color: $bh-gray;
    }
    .v-icon {
      color: $bh-blue;
    }
  }
  .v-breadcrumbs__item {
    max-width: 220px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    display: inline-block;
  }
}
@mixin ballb($yaxis: 0) {
  transform: translate3d(0, $yaxis, 0);
}

@keyframes bouncein {
  0%,
  40%,
  80% {
    @include ballb(-10px);
  }
  20%,
  60%,
  100% {
    @include ballb();
  }
}

.bounce {
  animation: bouncein 700ms cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}
.page-notifications-grouped {
  &--empty {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: 2em;
    .icon {
      max-width: 160px;
    }
    &-title {
      margin-top: 1em;
      color: $bh-black;
      text-align: center;
      max-width: 300px;
    }
  }
  &-tabs {
    padding: 8px;
    background: #ffffff;
    margin-bottom: 12px;
    border-radius: 50px;
    .v-tab {
      background-color: $bh-blue;
      color: $bh-white !important;
      text-transform: none;
      letter-spacing: 0;
      font-size: 1em;
      font-weight: 700;
      border-radius: 20px;

      &:before {
        border-radius: 20px;
      }

      &:not(.v-tab--active) {
        background-color: transparent;
        color: $bh-black !important;
      }
    }
    .v-tabs-slider {
      background: transparent;
    }
    .v-tabs-bar {
      height: 40px;
    }
  }
  &-v-tabs-items {
    margin-bottom: 31px;
    border-radius: 8px;
    overflow: visible !important;

    &.theme--light.v-tabs-items {
      background-color: transparent !important;
    }
  }
}
</style>
